export interface KeyboardShortcut {
  key: string;
  description: string;
}

export interface KeyboardShortcutCategory {
  title: string;
  shortcuts: KeyboardShortcut[];
}

export const KEYBOARD_SHORTCUTS_CONFIG: KeyboardShortcutCategory[] = [
  {
    title: "Navigation",
    shortcuts: [
      { key: "mod+shift+d", description: "Back to dashboard" },
      { key: "alt+1", description: "Switch to Dom Tree" },
      { key: "alt+2", description: "Switch to Assets" },
      { key: "alt+3", description: "Switch to Community" },
      { key: "alt+4", description: "Switch to File Selector" },
      { key: "alt+5", description: "Switch to Git Settings" },
      { key: "alt+6", description: "Switch to History" },
      { key: "alt+7", description: "Switch to App Store" },
      { key: "alt+8", description: "Switch to Info" },
      { key: "alt+9", description: "Switch to Project Settings" },
    ],
  },
  {
    title: "Zoom",
    shortcuts: [
      { key: "mod+=", description: "Zoom in" },
      { key: "mod+-", description: "Zoom out" },
      { key: "mod+0", description: "Reset zoom" },
      { key: "shift+1", description: "Zoom to fit" },
      { key: "shift+2", description: "Zoom to selection" },
    ],
  },
  {
    title: "Selection",
    shortcuts: [
      { key: "mod+a", description: "Select all elements" },
      { key: "tab", description: "Select next sibling" },
      { key: "shift+tab", description: "Select previous sibling" },
      { key: "\\", description: "Select parent" },
      { key: "escape", description: "Clear selection" },
    ],
  },
  {
    title: "Tools",
    shortcuts: [
      { key: "a", description: "AI tool" },
      { key: "v", description: "Select tool" },
      { key: "t", description: "Text tool" },
      { key: "h", description: "Hand tool" },
      { key: "f", description: "Draw tool" },
      { key: "g", description: "Generate image" },
      { key: "x", description: "Switch to light/dark mode" },
      { key: "d", description: "Design/Interactive mode" },
    ],
  },
  {
    title: "Text Formatting",
    shortcuts: [
      { key: "mod+b", description: "Toggle bold" },
      { key: "mod+i", description: "Toggle italic" },
      { key: "mod+u", description: "Toggle underline" },
      { key: "mod+shift+x", description: "Toggle strikethrough" },
      { key: "mod+alt+l", description: "Text align left" },
      { key: "mod+alt+t", description: "Text align center" },
      { key: "mod+alt+r", description: "Text align right" },
      { key: "mod+alt+j", description: "Text align justified" },
      { key: "shift+mod+,", description: "Decrease font size" },
      { key: "shift+mod+.", description: "Increase font size" },
      { key: "mod+alt+,", description: "Decrease font weight" },
      { key: "mod+alt+.", description: "Increase font weight" },
      { key: "alt+,", description: "Decrease letter spacing" },
      { key: "alt+.", description: "Increase letter spacing" },
      { key: "shift+alt+,", description: "Decrease line height" },
      { key: "shift+alt+.", description: "Increase line height" },
    ],
  },
  {
    title: "Layout & Style",
    shortcuts: [
      { key: "shift+a", description: "Add auto layout" },
      { key: "alt+shift+a", description: "Remove auto layout" },
      { key: "alt+/", description: "Remove fill" },
      { key: "shift+/", description: "Remove stroke" },
    ],
  },
  {
    title: "Canvas Actions",
    shortcuts: [
      { key: "mod+z", description: "Undo" },
      { key: "mod+shift+z", description: "Redo" },
      { key: "mod+d", description: "Duplicate selected element" },
      { key: "backspace", description: "Delete selected element" },
      {
        key: "mod+alt+k",
        description: "Create component from selected element",
      },
      { key: "mod+alt+b", description: "Detach component instance" },
      { key: "mod+alt+g", description: "Group (wrap) selected elements" },
      { key: "mod+shift+r", description: "Paste to replace selected elements" },
    ],
  },
  {
    title: "Positioning",
    shortcuts: [
      { key: "ArrowLeft", description: "Nudge left" },
      { key: "ArrowRight", description: "Nudge right" },
      { key: "ArrowUp", description: "Nudge up" },
      { key: "ArrowDown", description: "Nudge down" },
      { key: "shift+ArrowLeft", description: "Nudge left (large)" },
      { key: "shift+ArrowRight", description: "Nudge right (large)" },
      { key: "shift+ArrowUp", description: "Nudge up (large)" },
      { key: "shift+ArrowDown", description: "Nudge down (large)" },
    ],
  },
];

const formatKey = (key: string, isMac: boolean): string => {
  const trimmed = key.toLowerCase();

  switch (trimmed) {
    case "mod":
      return isMac ? "⌘" : "Ctrl";
    case "alt":
      return isMac ? "⌥" : "Alt";
    case "shift":
      return isMac ? "⇧" : "Shift";
    case "arrowleft":
      return "◀";
    case "arrowright":
      return "▶";
    case "arrowup":
      return "▲";
    case "arrowdown":
      return "▼";
    case "escape":
      return "Esc";
    case "backspace":
      return "⌫";
    case "tab":
      return "Tab";
    case "=":
      return "+";
    case "-":
      return "−";
    case "/":
      return "/";
    case ",":
      return ",";
    case ".":
      return ".";
    case "\\":
      return "\\";
    default:
      return key.toUpperCase();
  }
};

export const KEYBOARD_SHORTCUT_DISPLAY_HELPERS = {
  parseKeyString: (key: string): string[] => {
    return key.split("+").map((k) => k.trim());
  },
  formatKey,
  formatKeyboardShortcut: (keys: string[], isMac: boolean): string => {
    const separator = isMac ? "" : " + ";
    const formattedKeys = keys.map((key) => formatKey(key, isMac));
    return formattedKeys.join(separator);
  },
};
